import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useUserPersona } from '../context/UserPersonaContext';
import registrationService from '../services/registrationService';
import userLookupService from '../services/userLookupService';
import { getApiBaseUrlWithFallback } from '../config/environment';
import { getPublicInterclubFeatureToggles } from '../services/interclubFeatureToggleService';
import { fetchClubs } from '../services/clubService';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Checkbox,
  FormControlLabel,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import SearchIcon from '@mui/icons-material/Search';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoginIcon from '@mui/icons-material/Login';
import { debugLog } from '../utils/debug';
import {
  mergeAdultLevelsIntoSupportedOptions,
  ORDERED_SKATING_LEVELS,
  isAdultTrackLevelAboveBasicSix,
} from '../utils/skatingLevels';
import { isSeasonRegistrationOpen } from '../utils/seasonRegistration';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Season can be single-interclub (interclubId) or multi-interclub series (interclubIds)
const getSeasonInterclubIds = (season) =>
  Array.isArray(season?.interclubIds) && season.interclubIds.length > 0
    ? season.interclubIds
    : season?.interclubId
      ? [season.interclubId]
      : [];

const formatSeasonPaymentDate = (value) => {
  if (!value) return null;
  const date = value._seconds != null
    ? new Date(value._seconds * 1000)
    : typeof value.toDate === 'function'
      ? value.toDate()
      : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export default function Registration() {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    user, 
    loading: authLoading, 
    signInWithGoogle, 
    signUpWithEmail,
    signInWithEmail,
    getIdToken
  } = useFirebaseAuth();
  const { getNavigationLabels } = useUserPersona();

  // Get dynamic labels based on persona
  const navLabels = getNavigationLabels();

  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Sign up form state
  const [signUpForm, setSignUpForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    membershipNumber: '',
    phone: ''
  });

  // Sign in form state
  const [signInForm, setSignInForm] = useState({
    email: '',
    password: ''
  });

  // Lookup state
  const [lookupData, setLookupData] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);

  // User data state
  const [profile, setProfile] = useState(null);
  const [skaters, setSkaters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seasonInfo, setSeasonInfo] = useState(null);
  const [interclubInfo, setInterclubInfo] = useState(null);
  const [seasonFee, setSeasonFee] = useState(null);
  const [selectedSkaterIds, setSelectedSkaterIds] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeError, setFeeError] = useState(null);
  const [interclubToggles, setInterclubToggles] = useState({
    enableSeasonFeePayments: true,
  });
  const isSuccess = location.pathname.includes('/registration/season-fee/success');
  const isCancel = location.pathname.includes('/registration/season-fee/cancel');
  const sessionIdParam = new URLSearchParams(location.search).get('session_id');
  const [seasonDrafts, setSeasonDrafts] = useState({});
  const [clubs, setClubs] = useState([]);
  const [feeDataLoaded, setFeeDataLoaded] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  // Interclub supportedLevels may omit adult test levels; always offer Adult 1–6 + Gold for profiles
  const levelOptions = useMemo(() => {
    if (!interclubInfo?.supportedLevels?.length) return ORDERED_SKATING_LEVELS;
    const filtered = ORDERED_SKATING_LEVELS.filter((level) =>
      interclubInfo.supportedLevels.includes(level)
    );
    return mergeAdultLevelsIntoSupportedOptions(filtered, ORDERED_SKATING_LEVELS);
  }, [interclubInfo]);

  const seasonFeeLevelOptions = useMemo(
    () => levelOptions.filter((level) => !isAdultTrackLevelAboveBasicSix(level)),
    [levelOptions]
  );

  const loadUserData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Load profile and skaters in parallel
      const [profileResponse, skatersResponse] = await Promise.all([
        registrationService.getProfile(token),
        registrationService.getSkaters(token)
      ]);

      setProfile(profileResponse);
      setSkaters(skatersResponse || []);
      
      debugLog('User data loaded:', { profile: profileResponse, skaters: skatersResponse });
    } catch (error) {
      debugLog('Failed to load user data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  // Load user profile and skaters when authenticated
  useEffect(() => {
    if (user && !profile) {
      loadUserData();
    }
  }, [user, profile, loadUserData]);

  const loadSeasonFeeData = useCallback(async (forceReload = false) => {
    if (!user) return;
    // Don't reload if already loaded, unless forced
    if (feeDataLoaded && !forceReload) return;
    
    try {
      setFeeLoading(true);
      setFeeError(null);
      const baseUrl = await getApiBaseUrlWithFallback();
      const seasonsResponse = await fetch(`${baseUrl}/seasons?current=true`);
      if (!seasonsResponse.ok) {
        setFeeDataLoaded(true);
        return;
      }
      const seasons = await seasonsResponse.json();
      const currentSeasons = seasons.filter((season) => season.isCurrent);
      const userInterclubIds = new Set(skaters.map((skater) => skater.interclubId).filter(Boolean));
      let currentSeason = null;
      if (userInterclubIds.size > 0) {
        currentSeason = currentSeasons.find((season) => {
          const seasonIds = getSeasonInterclubIds(season);
          return seasonIds.some((id) => userInterclubIds.has(id));
        });
      }
      currentSeason = currentSeason || currentSeasons[0] || seasons[0];
      if (!currentSeason) {
        setFeeDataLoaded(true);
        return;
      }
      setSeasonInfo(currentSeason);

      if (currentSeason.interclubId) {
        const interclubResponse = await fetch(`${baseUrl}/interclubs/${currentSeason.interclubId}`);
        if (interclubResponse.ok) {
          const interclub = await interclubResponse.json();
          setInterclubInfo(interclub);
          const fee = currentSeason.seasonEntryFee ?? interclub.seasonEntryFee ?? null;
          setSeasonFee(fee);
        }

        try {
          const toggles = await getPublicInterclubFeatureToggles(currentSeason.interclubId);
          setInterclubToggles({
            enableSeasonFeePayments: toggles.enableSeasonFeePayments !== false,
          });
        } catch (toggleError) {
          console.error('Failed to load interclub toggles:', toggleError);
        }
      }

      const token = await getIdToken();
        if (token) {
          const skaterIds = skaters.map((skater) => skater.id).filter(Boolean);
          const registrationData = await registrationService.getSeasonRegistrations(currentSeason.id, token, skaterIds);
          setRegistrations(registrationData);
        }
      
      setFeeDataLoaded(true);
    } catch (error) {
      console.error('Failed to load season fee data:', error);
      setFeeError('Unable to load season fee information.');
      setFeeDataLoaded(true);
    } finally {
      setFeeLoading(false);
    }
  }, [user, getIdToken, skaters, feeDataLoaded]);

  // Load season fee data once when user and skaters are available
  useEffect(() => {
    if (user && skaters.length > 0 && !feeDataLoaded) {
      loadSeasonFeeData();
    }
  }, [user, skaters.length, feeDataLoaded, loadSeasonFeeData]);

  // Handle payment confirmation - runs once on success page
  useEffect(() => {
    const confirmPayment = async () => {
      if (!isSuccess || !sessionIdParam || !user || paymentConfirmed) return;
      
      setPaymentConfirmed(true); // Prevent duplicate confirmation attempts
      
      try {
        const token = await getIdToken();
        if (!token) return;
        await registrationService.confirmSeasonFeePayment(
          sessionIdParam,
          token,
          interclubInfo?.stripeAccountId
        );
        // Force reload registrations after successful payment
        setFeeDataLoaded(false);
      } catch (confirmError) {
        console.error('Failed to confirm season payment:', confirmError);
      }
    };
    confirmPayment();
  }, [isSuccess, sessionIdParam, user, getIdToken, interclubInfo, paymentConfirmed]);

  const paidSkaterIds = new Set(registrations.map((registration) => registration.skaterId));
  const paidAtBySkaterId = useMemo(() => {
    const map = {};
    registrations.forEach((registration) => {
      if (!registration.skaterId) return;
      map[registration.skaterId] = registration.paidAt || registration.paymentDate || null;
    });
    return map;
  }, [registrations]);
  const seasonInterclubIds = getSeasonInterclubIds(seasonInfo);
  const eligibleSkaters =
    seasonInterclubIds.length > 0
      ? skaters.filter((skater) => seasonInterclubIds.includes(skater.interclubId))
      : skaters;
  // Pre-Preliminary skaters without eligibility acknowledgment (must complete on Skaters page before checkout)
  const prePrelimNeedsAck = eligibleSkaters.filter(
    (s) =>
      (s.currentLevel === 'Pre-Preliminary' || s.startingLevel === 'Pre-Preliminary') &&
      !s.prePrelimAcknowledgedAt
  );
  const showPrePrelimAckNotice = prePrelimNeedsAck.length > 0;

  const adultTrackAboveBasicSixSkaters = useMemo(
    () =>
      eligibleSkaters.filter((s) =>
        isAdultTrackLevelAboveBasicSix(s.currentLevel || s.startingLevel || '')
      ),
    [eligibleSkaters]
  );
  const showAdultTrackSeasonBlockNotice = adultTrackAboveBasicSixSkaters.length > 0;

  // Helper to check skater status
  const getSkaterStatus = (skater) => {
    if (!seasonInfo?.id) return { isPaused: false, isDisqualified: false };
    const isPaused = (skater.pausedSeasons || []).includes(seasonInfo.id);
    const isDisqualified = (skater.disqualifiedSeasons || []).includes(seasonInfo.id);
    return { isPaused, isDisqualified };
  };

  const validateSeasonDraft = (draft, skater) => {
    const missing = [];
    // Check membershipId from skater or draft (if they added it during registration)
    if (!skater?.membershipId && !draft?.membershipId) missing.push('USFS ID');
    if (!draft?.startingLevel) missing.push('starting level');
    if (!draft?.coachName) missing.push('coach');
    if (!draft?.clubId && !draft?.club) missing.push('club');
    return missing;
  };

  const getSeasonLevelBlockMessage = (draft) => {
    const level = draft?.startingLevel;
    if (isAdultTrackLevelAboveBasicSix(level)) {
      return (
        'Season registration is only for Adult 1–6 on the adult test track. ' +
        'Adult Beginner and higher levels are not eligible for this series.'
      );
    }
    return null;
  };

  const hasDraftErrors = selectedSkaterIds.some((skaterId) => {
    const skater = eligibleSkaters.find((s) => s.id === skaterId);
    const draft = seasonDrafts[skaterId];
    return (
      validateSeasonDraft(draft, skater).length > 0 || Boolean(getSeasonLevelBlockMessage(draft))
    );
  });

  useEffect(() => {
    const loadClubs = async () => {
      const ids = getSeasonInterclubIds(seasonInfo);
      if (ids.length === 0) {
        setClubs([]);
        return;
      }
      try {
        const clubList = await fetchClubs(ids);
        setClubs(clubList);
      } catch (clubError) {
        console.error('Failed to load clubs:', clubError);
      }
    };
    loadClubs();
  }, [seasonInfo]);

  useEffect(() => {
    if (!seasonInfo?.id || eligibleSkaters.length === 0) return;
    setSeasonDrafts((prev) => {
      const next = { ...prev };
      eligibleSkaters.forEach((skater) => {
        if (!next[skater.id]) {
          const seasonEntry = skater.seasonLevels?.[seasonInfo.id] || {};
          next[skater.id] = {
            startingLevel: seasonEntry.startingLevel || skater.currentLevel || skater.startingLevel || '',
            coachName: seasonEntry.coachName || skater.coachName || '',
            clubId: seasonEntry.clubId || skater.clubId || '',
            club: seasonEntry.club || skater.club || '',
            interclubId: seasonEntry.interclubId || skater.interclubId || (seasonInfo?.interclubIds?.[0] ?? seasonInfo?.interclubId) || '',
          };
        }
      });
      return next;
    });
  }, [seasonInfo, eligibleSkaters]);

  const toggleSkaterSelection = (skaterId) => {
    setSelectedSkaterIds((prev) =>
      prev.includes(skaterId) ? prev.filter((id) => id !== skaterId) : [...prev, skaterId]
    );
  };

  const updateSeasonDraft = (skaterId, updates) => {
    setSeasonDrafts((prev) => ({
      ...prev,
      [skaterId]: {
        ...(prev[skaterId] || {}),
        ...updates,
      },
    }));
  };

  const handleSeasonFeeCheckout = async () => {
    try {
      setFeeError(null);
      const token = await getIdToken();
      if (!token) {
        throw new Error('Missing authentication token');
      }
      if (selectedSkaterIds.length === 0) {
        throw new Error('Select at least one skater');
      }
      if (!seasonInfo?.id) {
        throw new Error('Season not available');
      }

      const draftIssues = selectedSkaterIds
        .map((skaterId) => {
          const draft = seasonDrafts[skaterId];
          const skater = eligibleSkaters.find((s) => s.id === skaterId);
          const missing = validateSeasonDraft(draft, skater);
          return missing.length > 0 ? { skaterId, missing } : null;
        })
        .filter(Boolean);
      if (draftIssues.length > 0) {
        throw new Error('Complete season registration details for selected skaters.');
      }

      const blockedLevel = selectedSkaterIds
        .map((skaterId) => getSeasonLevelBlockMessage(seasonDrafts[skaterId]))
        .find(Boolean);
      if (blockedLevel) {
        throw new Error(blockedLevel);
      }

      // Update skaters with membershipId if provided in draft
      const membershipUpdates = selectedSkaterIds
        .filter((skaterId) => {
          const draft = seasonDrafts[skaterId];
          const skater = eligibleSkaters.find((s) => s.id === skaterId);
          return draft?.membershipId && !skater?.membershipId;
        })
        .map((skaterId) => {
          const draft = seasonDrafts[skaterId];
          return registrationService.updateSkater(
            skaterId,
            { membershipId: draft.membershipId },
            token
          );
        });
      if (membershipUpdates.length > 0) {
        await Promise.all(membershipUpdates);
      }

      const updatePromises = selectedSkaterIds.map((skaterId) => {
        const draft = seasonDrafts[skaterId] || {};
        const clubInfo = clubs.find((club) => club.id === draft.clubId);
        return registrationService.updateSkater(
          skaterId,
          {
            seasonId: seasonInfo.id,
            startingLevel: draft.startingLevel,
            currentLevel: draft.startingLevel,
            levelChanged: false,
            coachName: draft.coachName,
            clubId: draft.clubId || '',
            club: draft.club || clubInfo?.name || '',
            interclubId: draft.interclubId || clubInfo?.interclubId || (seasonInfo?.interclubIds?.[0] ?? seasonInfo?.interclubId) || '',
          },
          token
        );
      });
      await Promise.all(updatePromises);

      const payload = {
        seasonId: seasonInfo.id,
        skaterIds: selectedSkaterIds,
        successUrl: `${window.location.origin}/registration/season-fee/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/registration/season-fee/cancel`,
      };
      const session = await registrationService.createSeasonFeeCheckout(payload, token);
      if (session?.url) {
        window.location.assign(session.url);
      } else {
        throw new Error('Checkout session not created');
      }
    } catch (error) {
      console.error('Season fee checkout failed:', error);
      setFeeError(error.message || 'Unable to start checkout');
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError(null);
    setLookupError(null);
  };

  // Handle sign up form changes
  const handleSignUpChange = (field) => (event) => {
    setSignUpForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Handle sign in form changes
  const handleSignInChange = (field) => (event) => {
    setSignInForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Handle Google sign in
  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      await signInWithGoogle();
    } catch (error) {
      setError(error.message);
    }
  };

  // Handle email sign up
  const handleEmailSignUp = async () => {
    try {
      setError(null);
      
      // Validate form
      if (signUpForm.password !== signUpForm.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (signUpForm.password.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }

      if (!signUpForm.membershipNumber) {
        setError('US Figure Skating membership number is required');
        return;
      }

      const createdUser = await signUpWithEmail(
        signUpForm.email, 
        signUpForm.password, 
        signUpForm.firstName, 
        signUpForm.lastName
      );

      if (createdUser) {
        const token = await createdUser.getIdToken();
        await registrationService.updateProfile(
          {
            firstName: signUpForm.firstName,
            lastName: signUpForm.lastName,
            phone: signUpForm.phone,
            membershipNumber: signUpForm.membershipNumber
          },
          token
        );
      }
    } catch (error) {
      setError(error.message);
    }
  };

  // Handle email sign in
  const handleEmailSignIn = async () => {
    try {
      setError(null);
      await signInWithEmail(signInForm.email, signInForm.password);
    } catch (error) {
      setError(error.message);
    }
  };

  // Handle membership number lookup
  const handleMembershipLookup = async () => {
    try {
      setLookupLoading(true);
      setLookupError(null);
      
      const data = await userLookupService.lookupByMembershipNumber(signUpForm.membershipNumber);
      setLookupData(data);
    } catch (error) {
      setLookupError(error.message);
    } finally {
      setLookupLoading(false);
    }
  };

  // Navigation handler
  const handleNavigateToSkaters = useCallback(() => {
    navigate('/skaters');
  }, [navigate]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ width: '100%', maxWidth: 'none', pl: 0 }}>
        <Paper elevation={0} sx={{ overflow: 'hidden', maxWidth: 800, mx: 'auto', borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          {/* Header */}
          <Box sx={{ 
            p: { xs: 3, sm: 5 }, 
            textAlign: 'center', 
            background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
          }}>
            <Box sx={{ 
              display: 'inline-flex', 
              p: 2, 
              borderRadius: '50%', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              mb: 2,
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
            }}>
              <PersonAddIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: 'text.primary' }}>
              {navLabels.registration}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Sign in or create an account to manage your skaters
            </Typography>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="authentication tabs">
              <Tab label="Sign In" />
              <Tab label="Sign Up" />
            </Tabs>
          </Box>

          {/* Error Display */}
          {(error || lookupError) && (
            <Alert severity="error" sx={{ m: 3 }} onClose={() => { setError(null); setLookupError(null); }}>
              {error || lookupError}
            </Alert>
          )}

          {/* Sign In Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ maxWidth: 400, mx: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 1.5, 
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  display: 'flex',
                  boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)'
                }}>
                  <LoginIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Sign In to Your Account
                </Typography>
              </Box>

              {/* Google Sign In */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                sx={{ 
                  mb: 3, 
                  py: 1.5, 
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 999,
                  textTransform: 'none',
                  backgroundColor: '#4285f4',
                  '&:hover': { backgroundColor: '#3367d6' }
                }}
              >
                Sign in with Google
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">OR</Typography>
              </Divider>

              {/* Email Sign In Form */}
              <form onSubmit={(e) => { e.preventDefault(); handleEmailSignIn(); }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={signInForm.email}
                  onChange={handleSignInChange('email')}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={signInForm.password}
                  onChange={handleSignInChange('password')}
                  margin="normal"
                  required
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{ 
                    mt: 3, 
                    py: 1.5,
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                    '&:hover': { 
                      background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                    }
                  }}
                >
                  Sign In
                </Button>
              </form>
            </Box>
          </TabPanel>

          {/* Sign Up Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ maxWidth: 600, mx: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 1.5, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}>
                  <PersonAddIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Create New Account
                </Typography>
              </Box>

              {/* Google Sign Up */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                sx={{ 
                  mb: 3, 
                  py: 1.5, 
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 999,
                  textTransform: 'none',
                  backgroundColor: '#4285f4',
                  '&:hover': { backgroundColor: '#3367d6' }
                }}
              >
                Sign up with Google
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">OR</Typography>
              </Divider>

              {/* Email Sign Up Form */}
              <form onSubmit={(e) => { e.preventDefault(); handleEmailSignUp(); }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={signUpForm.firstName}
                      onChange={handleSignUpChange('firstName')}
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={signUpForm.lastName}
                      onChange={handleSignUpChange('lastName')}
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={signUpForm.email}
                      onChange={handleSignUpChange('email')}
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={signUpForm.phone}
                      onChange={handleSignUpChange('phone')}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Figure Skating Membership Number"
                      value={signUpForm.membershipNumber}
                      onChange={handleSignUpChange('membershipNumber')}
                      margin="normal"
                      required
                      helperText={
                        <span>
                          Required. Find your USFS ID at{' '}
                          <Link
                            href="https://www.usfsaonline.org/Account/FindMembershipNumber"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            usfsaonline.org
                          </Link>
                          .
                        </span>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={handleMembershipLookup}
                      disabled={!signUpForm.membershipNumber || lookupLoading}
                      sx={{ mb: 2 }}
                    >
                      {lookupLoading ? 'Looking up...' : 'Look Up Existing Data'}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      value={signUpForm.password}
                      onChange={handleSignUpChange('password')}
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      type="password"
                      value={signUpForm.confirmPassword}
                      onChange={handleSignUpChange('confirmPassword')}
                      margin="normal"
                      required
                    />
                  </Grid>
                </Grid>

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{ 
                    mt: 3, 
                    py: 1.5,
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                    '&:hover': { 
                      background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                    }
                  }}
                >
                  Create Account
                </Button>
              </form>
            </Box>
          </TabPanel>
        </Paper>

        {/* Lookup Results Dialog */}
        <Dialog 
          open={lookupData !== null} 
          maxWidth="md" 
          fullWidth
          onClose={() => setLookupData(null)}
        >
          <DialogTitle>
            <Typography variant="h6">
              📋 Existing Data Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We found existing data for membership number: {signUpForm.membershipNumber}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {lookupData && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Please review and confirm the following information:
                </Typography>
                
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">First Name</Typography>
                    <Typography variant="body1">{lookupData.firstName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Last Name</Typography>
                    <Typography variant="body1">{lookupData.lastName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{lookupData.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">{lookupData.phone || 'Not provided'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Club</Typography>
                    <Typography variant="body1">{lookupData.club || 'Not specified'}</Typography>
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 2 }}>
                  This information will be pre-filled in your account. You can modify it after signing up.
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLookupData(null)}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={() => {
                if (lookupData) {
                  setSignUpForm(prev => ({
                    ...prev,
                    firstName: lookupData.firstName || prev.firstName,
                    lastName: lookupData.lastName || prev.lastName,
                    email: lookupData.email || prev.email,
                    phone: lookupData.phone || prev.phone
                  }));
                  setLookupData(null);
                }
              }}
            >
              Use This Data
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Check if user is a coach-only (coach without own skaters and not marked as parent)
  // Check both the isCoach toggle AND the roles array (from role approval system)
  const isCoach = Boolean(profile?.isCoach) || Boolean(profile?.roles?.includes('coach'));
  const isCoachOnly = Boolean(
    isCoach && 
    !profile?.isParent && 
    !profile?.isSkater && 
    skaters.length === 0
  );

  // Coach-only view - redirect to Dashboard where they can see their coached skaters
  if (isCoachOnly) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ 
              display: 'inline-flex', 
              p: 2, 
              borderRadius: '50%', 
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              mb: 2,
              boxShadow: '0 8px 24px rgba(17, 153, 142, 0.3)'
            }}>
              <PersonAddIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary' }}>
              Registration
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 600, mx: 'auto' }}>
              As a coach, you don't need to register or pay for skaters. 
              Parents register and pay for their own skaters directly.
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2">
              To track your students' progress and see their competition results, 
              visit your <strong>Dashboard</strong>.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              onClick={() => navigate('/dashboard')}
              sx={{ 
                borderRadius: 999, 
                textTransform: 'none', 
                fontWeight: 600,
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                }
              }}
            >
              Go to Dashboard
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />
          
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Are you also a parent?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If you have children who skate and need to register them for the season, 
            update your profile to indicate you're also a parent. This will allow you to 
            add new skaters and register them for competitions.
          </Typography>
          <Button 
            variant="text" 
            onClick={() => navigate('/profile')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Update Profile Settings →
          </Button>
        </Paper>
      </Box>
    );
  }

  // User is authenticated - show dashboard
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, mb: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ 
            display: 'inline-flex', 
            p: 2, 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            mb: 2,
            boxShadow: '0 8px 24px rgba(79, 172, 254, 0.3)'
          }}>
            <PaymentIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary' }}>
            {navLabels.registration}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isSeasonRegistrationOpen(seasonInfo)
              ? 'Enroll and pay for your skaters in the current season'
              : 'Create an account, add skaters, and view history. Season registration opens when the next season is ready.'}
          </Typography>
          {seasonFee && !feeLoading && !isCoachOnly && isSeasonRegistrationOpen(seasonInfo) && (
            <Typography variant="body2" sx={{ mt: 1, color: 'success.main', fontWeight: 600 }}>
              Season entry fee: ${seasonFee} per skater
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button 
            variant="outlined" 
            onClick={handleNavigateToSkaters}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
          >
            Manage Skaters
          </Button>
        </Box>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {isSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Payment received! Your skater registration will update shortly.
        </Alert>
      )}
      {isCancel && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Payment was canceled. You can try again anytime.
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}


      {/* Season Entry Fee - don't show for coach-only users (parents pay for their own skaters) */}
      {user && !isCoachOnly && interclubToggles.enableSeasonFeePayments && !isSeasonRegistrationOpen(seasonInfo) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Season registration is closed. You can still create an account, add skaters, and view past results.
          Registration will open after the next season is set up.
        </Alert>
      )}

      {user && interclubToggles.enableSeasonFeePayments && isSeasonRegistrationOpen(seasonInfo) && !isCoachOnly && (
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)'
            }}>
              <PaymentIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Season Entry Fee
            </Typography>
            {feeLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </Box>
          {feeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {feeError}
            </Alert>
          )}
          {showPrePrelimAckNotice && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Pre-Preliminary acknowledgment required
              </Typography>
              <Typography variant="body2">
                {prePrelimNeedsAck.length === 1 ? (
                  <>
                    <strong>{prePrelimNeedsAck[0].firstName} {prePrelimNeedsAck[0].lastName}</strong> is at Pre-Preliminary
                    level. Complete the eligibility acknowledgment on the{' '}
                    <Link component="button" variant="body2" onClick={() => navigate('/skaters')} sx={{ fontWeight: 600 }}>
                      Skaters
                    </Link>{' '}
                    page (Edit Skater → check the Pre-Preliminary acknowledgment box) before you can pay the season fee.
                  </>
                ) : (
                  <>
                    {prePrelimNeedsAck.length} of your skaters are at Pre-Preliminary level and need the eligibility
                    acknowledgment. Go to{' '}
                    <Link component="button" variant="body2" onClick={() => navigate('/skaters')} sx={{ fontWeight: 600 }}>
                      Skaters
                    </Link>{' '}
                    and complete the acknowledgment for each (Edit Skater → check the box, then save) before paying the season fee.
                  </>
                )}
              </Typography>
            </Alert>
          )}
          {showAdultTrackSeasonBlockNotice && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Season registration not available past Adult 6
              </Typography>
              <Typography variant="body2">
                {adultTrackAboveBasicSixSkaters.length === 1 ? (
                  <>
                    <strong>
                      {adultTrackAboveBasicSixSkaters[0].firstName}{' '}
                      {adultTrackAboveBasicSixSkaters[0].lastName}
                    </strong>{' '}
                    is listed at {adultTrackAboveBasicSixSkaters[0].currentLevel || adultTrackAboveBasicSixSkaters[0].startingLevel}.
                    This series only accepts Adult 1–6 on the adult test track. Update their level on the{' '}
                    <Link component="button" variant="body2" onClick={() => navigate('/skaters')} sx={{ fontWeight: 600 }}>
                      Skaters
                    </Link>{' '}
                    page if it is incorrect, or contact your club if you have questions.
                  </>
                ) : (
                  <>
                    {adultTrackAboveBasicSixSkaters.length} of your skaters are above Adult 6 on the adult test track
                    (Adult Beginner and higher) and cannot register for this series. Review each profile on{' '}
                    <Link component="button" variant="body2" onClick={() => navigate('/skaters')} sx={{ fontWeight: 600 }}>
                      Skaters
                    </Link>
                    .
                  </>
                )}
              </Typography>
            </Alert>
          )}
          {!feeDataLoaded && feeLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          {feeDataLoaded && seasonInfo && (
            <>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Season: <strong>{seasonInfo.name}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Interclub: {interclubInfo?.name || 'Not set'}
              </Typography>
              <Box sx={{ 
                display: 'inline-block', 
                bgcolor: seasonFee ? 'success.50' : 'grey.100', 
                border: '1px solid',
                borderColor: seasonFee ? 'success.main' : 'grey.300',
                borderRadius: 2, 
                px: 2, 
                py: 1, 
                mb: 2 
              }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: seasonFee ? 'success.dark' : 'text.secondary' }}>
                  Season Entry Fee: {seasonFee ? `$${seasonFee} per skater` : 'Not configured'}
                </Typography>
                {seasonFee && (
                  <Typography variant="caption" color="text.secondary">
                    Fee set by {interclubInfo?.name || 'interclub'}
                  </Typography>
                )}
              </Box>
              {seasonFee ? (
                <>
                  {eligibleSkaters.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No skaters found for this interclub. Add skaters from the Manage Skaters page.
                    </Typography>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Select skaters to register:
                      </Typography>
                      {eligibleSkaters.map((skater) => {
                        const isPaid = paidSkaterIds.has(skater.id);
                        const paidAtLabel = isPaid ? formatSeasonPaymentDate(paidAtBySkaterId[skater.id]) : null;
                        const draft = seasonDrafts[skater.id] || {};
                        const isSelected = selectedSkaterIds.includes(skater.id);
                        const missingFields = validateSeasonDraft(draft, skater);
                        const adultLevelBlockMessage = getSeasonLevelBlockMessage(draft);
                        const profileAdultLevelBlocksSeason = isAdultTrackLevelAboveBasicSix(
                          skater.currentLevel || skater.startingLevel || ''
                        );
                        const needsMembershipId = !skater.membershipId && !draft.membershipId;
                        const { isPaused, isDisqualified } = getSkaterStatus(skater);
                        const isIneligible = isPaused || isDisqualified;
                        const cannotRegisterSeason = profileAdultLevelBlocksSeason;
                        return (
                          <Box
                            key={skater.id}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              mb: 2,
                              opacity: isIneligible ? 0.5 : 1,
                              bgcolor: isIneligible ? 'grey.50' : 'transparent',
                              borderRadius: 1,
                              p: isIneligible ? 1 : 0,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => toggleSkaterSelection(skater.id)}
                                    disabled={isPaid || isIneligible || cannotRegisterSeason}
                                  />
                                }
                                label={
                                  <Box component="span" sx={{ color: isIneligible ? 'text.disabled' : 'text.primary' }}>
                                    {skater.firstName} {skater.lastName}
                                  </Box>
                                }
                              />
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {isPaid && (
                                  <>
                                    {paidAtLabel && (
                                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                        {paidAtLabel}
                                      </Typography>
                                    )}
                                    <Chip label="Paid" color="success" size="small" />
                                  </>
                                )}
                                {isDisqualified && (
                                  <Chip 
                                    label="Disqualified" 
                                    color="error" 
                                    size="small" 
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}
                                {isPaused && !isDisqualified && (
                                  <Chip 
                                    label="Paused" 
                                    color="default" 
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}
                              </Box>
                            </Box>
                            {isIneligible && (
                              <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mt: 0.5 }}>
                                {isDisqualified 
                                  ? 'This skater is disqualified. Contact competition coordinator.' 
                                  : 'This skater is paused. Contact competition coordinator.'}
                              </Typography>
                            )}
                            {cannotRegisterSeason && !isPaid && (
                              <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mt: 0.5 }}>
                                Season registration is only for Adult 1–6 on the adult test track. Update this skater’s
                                level on the Skaters page if it is wrong, or contact your club.
                              </Typography>
                            )}
                            {isSelected && !isPaid && (
                              <Box sx={{ pl: 4, pt: 1 }}>
                                {needsMembershipId && (
                                  <Alert severity="warning" sx={{ mb: 2 }}>
                                    USFS Membership ID is required for registration. Please enter it below.
                                  </Alert>
                                )}
                                <Grid container spacing={2}>
                                  {needsMembershipId && (
                                    <Grid item xs={12} md={4}>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label="USFS Membership ID"
                                        value={draft.membershipId || ''}
                                        onChange={(e) => updateSeasonDraft(skater.id, { membershipId: e.target.value })}
                                        required
                                        error={!draft.membershipId}
                                        placeholder="e.g., 1234567"
                                        helperText="Required for registration"
                                      />
                                    </Grid>
                                  )}
                                  <Grid item xs={12} md={needsMembershipId ? 4 : 4}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Starting Level</InputLabel>
                                      <Select
                                        label="Starting Level"
                                        value={draft.startingLevel || ''}
                                        onChange={(e) => updateSeasonDraft(skater.id, { startingLevel: e.target.value })}
                                      >
                                        <MenuItem value="">
                                          <em>Select level</em>
                                        </MenuItem>
                                        {seasonFeeLevelOptions.map((level) => (
                                          <MenuItem key={level} value={level}>
                                            {level}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Club</InputLabel>
                                      <Select
                                        label="Club"
                                        value={draft.clubId || ''}
                                        onChange={(e) => updateSeasonDraft(skater.id, { clubId: e.target.value })}
                                      >
                                        <MenuItem value="">
                                          <em>Select club</em>
                                        </MenuItem>
                                        {clubs.map((club) => (
                                          <MenuItem key={club.id} value={club.id}>
                                            {club.name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Coach"
                                      value={draft.coachName || ''}
                                      onChange={(e) => updateSeasonDraft(skater.id, { coachName: e.target.value })}
                                    />
                                  </Grid>
                                </Grid>
                                {missingFields.length > 0 && (
                                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                    Please add: {missingFields.join(', ')}.
                                  </Typography>
                                )}
                                {adultLevelBlockMessage && (
                                  <Alert severity="warning" sx={{ mt: 2 }}>
                                    <Typography variant="body2">{adultLevelBlockMessage}</Typography>
                                  </Alert>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Total: {selectedSkaterIds.length > 0 ? `$${seasonFee * selectedSkaterIds.length}` : '$0'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Registration is generally non-refundable. If a refund is approved, Stripe fees will be deducted.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={selectedSkaterIds.length === 0 || hasDraftErrors}
                    onClick={handleSeasonFeeCheckout}
                    sx={{ 
                      borderRadius: 999,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                      boxShadow: '0 4px 14px rgba(17, 153, 142, 0.4)',
                      '&:hover': { 
                        background: 'linear-gradient(135deg, #0f8b82 0%, #32d970 100%)',
                        boxShadow: '0 6px 20px rgba(17, 153, 142, 0.5)',
                      },
                      '&.Mui-disabled': {
                        background: 'grey.300',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    Pay Season Entry Fee
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Entry fee has not been configured yet.
                </Typography>
              )}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
